/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import PageNavigation from 'bundles/unified-description-page-common/components/modules/product-overview/PageNavigation';
import type { NavigationLink } from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useStickyPanel } from 'bundles/unified-description-page-common/contexts/StickyPanelContext';

type Props = {
  heroBackgroundColor?: string;
  hero: React.ReactNode;
  stickyPanel?: React.ReactNode;
  navigationLinks?: Array<NavigationLink>;
  anchorId?: string;
  aboutSubmodule?: React.ReactNode;
};

const styles = {
  sectionContainer: css`
    position: relative;

    /* fallback to hidden if clip is not supported */
    overflow-x: hidden;
    overflow-x: clip;
  `,
  heroContainer: css`
    position: relative;
  `,
  heroBackground: (heroBackgroundColor?: string) => css`
    background-color: ${heroBackgroundColor ?? 'transparent'};
    position: absolute;
    width: 200vw;
    left: -50vw;
    top: 0;
    bottom: 0;
    z-index: -1;
  `,
  stickyPanelContainer: css`
    position: relative;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  stickyPanel: (zIndex?: number) => css`
    position: sticky;
    top: 150px;
    ${zIndex !== undefined &&
    `
      z-index: ${zIndex};
    `}
  `,
  stickyPanelInner: css`
    background: var(--cds-color-white-0);
    border-radius: var(--cds-spacing-100);
    box-shadow: 0 0 32px -8px rgb(0 0 0 / 50%);
  `,
  mobileStickyPanelContainer: css`
    display: none;
    ${breakpoints.down('sm')} {
      display: block;
    }
  `,
};

const ProductOverviewModule = ({
  heroBackgroundColor,
  hero,
  stickyPanel,
  navigationLinks,
  anchorId,
  aboutSubmodule,
}: Props) => {
  const { zIndex: stickyPanelZIndex } = useStickyPanel();
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'about',
      },
      ...eventingProductData,
    }
  );

  return (
    <section css={styles.sectionContainer}>
      <PageGridContainer>
        <Grid item xs={12} md={8}>
          <div css={styles.heroContainer}>
            {hero}
            {/*
             * The background color of the hero banner needs to flush all the way to the edge of the browser window.
             * The div below is absolute-positioned with a height of its parent container and a width of 200vw.
             * To prevent the horizontal scrollbar appears because of the 200vw width,
             * overflow-x: clip is added to the top most element of the component.
             */}
            <div css={styles.heroBackground(heroBackgroundColor)} />
          </div>
          <div css={styles.mobileStickyPanelContainer}>{stickyPanel}</div>
          <PageNavigation navigationLinks={navigationLinks} />
          {aboutSubmodule && (
            <Section anchorId={anchorId} spacing={0} ref={visibilityTrackingRef}>
              {aboutSubmodule}
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={4} css={styles.stickyPanelContainer} data-e2e="key-information">
          <div css={styles.stickyPanel(stickyPanelZIndex)}>
            <div css={styles.stickyPanelInner}>{stickyPanel}</div>
          </div>
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default ProductOverviewModule;
